import { Group, GroupSubscription, formatNumbersStr } from '@intly/core';
import { Banner, Card } from '@intly/design';
import {
  Box,
  Button,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { DateTime } from 'luxon';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IoHelpCircleOutline, IoPencil } from 'react-icons/io5';
import InvoiceStatusChip from './InvoiceStatusChip';

const Item: FC<{ label: string; value: string; element?: ReactNode }> = ({
  label,
  value,
  element,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>

      {element ? element : <Typography>{value}</Typography>}
    </Box>
  );
};

interface HelperSubscription {
  subscription: GroupSubscription;
  extraUsers: number;
  extraEmps: number;
  extras: GroupSubscription[];
}

interface Props {
  group?: Group;
  onEdit?: () => void;
  onShowInvoices?: () => void;
  hideInvoice?: boolean;
}

const SubscriptionSummary: FC<Props> = ({
  group,
  onEdit,
  onShowInvoices,
  hideInvoice,
}) => {
  const { t } = useTranslation();

  const sub = group?.subscriptions?.subscriptions
    .filter((sub) => sub.isActive)
    .reduce<HelperSubscription>(
      (acc, sub) => {
        if (!sub.extra) {
          acc.subscription = sub;
        } else {
          acc.extras.push(sub);
          if (sub.price.users) {
            acc.extraUsers += sub.price.users;
          }
          if (sub.price.employees) {
            acc.extraEmps += sub.price.employees;
          }
        }
        return acc;
      },
      { extras: [], extraUsers: 0, extraEmps: 0 } as any
    );

  const nexRenewDate = (subStart: number, interval: number) => {
    const today = DateTime.now();
    let start = DateTime.fromSeconds(subStart);
    let end = start.plus({ months: interval });
    while ((today >= start && today < end) || end < today) {
      start = end;
      end = start.plus({ months: interval });
    }

    return start.toFormat('yyyy-MM-dd');
  };

  const lastInvoice = [...(group?.invoices ? group.invoices : [])]?.sort(
    (a, b) => b.invoicePeriodStart.localeCompare(a.invoicePeriodStart)
  )?.[0];

  if (!sub) {
    return null;
  }

  if (!sub?.subscription && group) {
    return (
      <Card
        sx={{
          border: '1px solid',
          borderColor: 'border',
        }}
      >
        <Banner severity="info" message={t('NO_ACTIVE_SUBSCRIPTION')} />
      </Card>
    );
  }

  return (
    <Grid2 container columns={12} spacing={2} mt={2}>
      <Grid2 xs={12} sm={!hideInvoice ? 8 : 12}>
        <Card
          lineHeader={t('SUBSCRIPTION')}
          padding={2}
          sx={{
            border: '1px solid',
            borderColor: 'border',
            height: 1,
          }}
        >
          <Grid2 container rowSpacing={0} columnSpacing={2} columns={12}>
            <Grid2 xs={4}>
              <Item label={t('PLAN')} value={sub.subscription.name} />
            </Grid2>
            <Grid2 xs={4}>
              <Item
                label={t('INVOICE_PERIOD')}
                value={`${sub.subscription.price.interval} månader`}
              />
            </Grid2>

            <Grid2 xs={4}>
              <Item
                label="Antal användare"
                value={`${
                  sub.subscription.price.users + sub.extraUsers
                } st (extra: ${sub.extraUsers})`}
              />
            </Grid2>
            <Grid2 xs={4}>
              <Item
                label={t('STARTED')}
                value={DateTime.fromSeconds(sub.subscription.start).toFormat(
                  'yyyy-MM-dd'
                )}
              />
            </Grid2>

            <Grid2 xs={4}>
              {sub.subscription.end || !sub.subscription.price.renew ? (
                <Item
                  label={t('ENDING')}
                  value={
                    sub.subscription.end
                      ? DateTime.fromSeconds(sub.subscription.end).toFormat(
                          'yyyy-MM-dd'
                        )
                      : DateTime.fromSeconds(sub.subscription.start)
                          .plus({ months: sub.subscription.price.interval })
                          .toFormat('yyyy-MM-dd')
                  }
                />
              ) : (
                <Item
                  label={t('AUTOMATIC_RENEWAL')}
                  value={nexRenewDate(
                    sub.subscription.start,
                    sub.subscription.price.interval
                  )}
                />
              )}
            </Grid2>

            <Grid2 xs={4}>
              {Boolean(sub.subscription.price.employees) && (
                <Item
                  label="Antal anställda"
                  value={`${
                    sub.subscription.price.employees + sub.extraEmps
                  } st (extra: ${sub.extraEmps})`}
                />
              )}
            </Grid2>

            <Grid2 xs={4}>
              <Item
                label={
                  sub.subscription.price.isPrivate
                    ? `${t('PRICE')} ${t('SUBTOTAL_BRUTTO').replace(
                        'Totalt',
                        ''
                      )}`
                    : `${t('PRICE')} ${t('SUBTOTAL_NET').replace('Totalt', '')}`
                }
                value={
                  sub.subscription.price.isPrivate
                    ? `${formatNumbersStr(sub.subscription.price.total)} kr`
                    : `${formatNumbersStr(sub.subscription.price.totalNet)} kr`
                }
              />
            </Grid2>

            <Grid2
              xs={4}
              sx={{
                textAlign: 'left',
              }}
            >
              {onEdit && (
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  endIcon={<IoPencil />}
                  onClick={onEdit}
                >
                  {t('MANAGE')}
                </Button>
              )}
            </Grid2>

            <Grid2 xs={4}>
              {Boolean(sub.subscription.price.years) && (
                <Item
                  label="Antal år (lagring av data)"
                  value={`${sub.subscription.price.years} år`}
                />
              )}
            </Grid2>
          </Grid2>
        </Card>
      </Grid2>

      {!hideInvoice && (
        <Grid2 xs={12} sm={4}>
          <Card
            padding={2}
            lineHeader={t('LAST_INVOICE')}
            sx={{ border: '1px solid', borderColor: 'border', height: 1 }}
          >
            {lastInvoice ? (
              <>
                <Typography variant="caption">
                  {lastInvoice.invoicePeriodStart} →{' '}
                  {lastInvoice.invoicePeriodEnd}
                </Typography>
                <Item
                  label={t('STATUS')}
                  value=""
                  element={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <InvoiceStatusChip status={lastInvoice.status} />
                      <Tooltip title=" Det kan ta upp till 5 arbetsdagar från betalning tills du ser den här">
                        <Box sx={{ display: 'flex' }}>
                          <SvgIcon
                            inheritViewBox
                            component={IoHelpCircleOutline}
                          />
                        </Box>
                      </Tooltip>
                    </Stack>
                  }
                />
                <Item
                  label={t('AMOUNT')}
                  value={`${formatNumbersStr(lastInvoice.amount)} kr`}
                />
                {['unpaid', 'overdue'].includes(lastInvoice.status) && (
                  <Item label={t('DUE_DATE')} value={lastInvoice.dueDate} />
                )}
                <Typography variant="caption"></Typography>
                {onShowInvoices && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    endIcon={<IoPencil />}
                    onClick={onShowInvoices}
                  >
                    {t('SHOW_INVOICES')}
                  </Button>
                )}
              </>
            ) : (
              <Banner severity="info" message={t('NO_INVOICES')} />
            )}
          </Card>
        </Grid2>
      )}
    </Grid2>
  );
};

export default SubscriptionSummary;
